import { useState, useRef, useCallback, useEffect } from "react";

/**
 * useRecorder — Enregistrement local du PROGRAM output.
 * 
 * Option A: MediaRecorder API — accumule en mémoire puis télécharge
 * Option B: File System Access API (Chrome/Edge) — écrit en continu sur disque
 * 
 * Combine les deux : si File System Access est disponible, écrit directement
 * sur le disque choisi par l'opérateur. Sinon, accumule et télécharge.
 */

const SEGMENT_DURATION_MS = 120_000; // 2 minutes par segment (mode File System)
const TIMESLICE_MS = 2000; // ondataavailable toutes les 2s

function getCodec() {
  // Préférer VP9 pour meilleure qualité, sinon H264, sinon VP8
  const codecs = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=h264,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const c of codecs) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

function supportsFileSystemAccess() {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

function formatDuration(ms) {
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function formatSize(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}

export default function useRecorder() {
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [fileSize, setFileSize] = useState(0);
  const [segmentCount, setSegmentCount] = useState(0);
  const [mode, setMode] = useState("memory"); // "memory" | "filesystem"
  const [codec, setCodec] = useState("");
  const [quality, setQuality] = useState("1080p"); // "720p" | "1080p" | "1440p" | "4k"
  const [error, setError] = useState(null);
  const [fsSupported] = useState(supportsFileSystemAccess());

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(null);
  const durationTimerRef = useRef(null);
  const fileSizeRef = useRef(0);
  const dirHandleRef = useRef(null);
  const currentWriterRef = useRef(null);
  const segmentIndexRef = useRef(0);
  const segmentTimerRef = useRef(null);
  const streamRef = useRef(null);

  // Quality presets
  const qualityMap = {
    "720p": { width: 1280, height: 720, videoBitsPerSecond: 2_500_000 },
    "1080p": { width: 1920, height: 1080, videoBitsPerSecond: 5_000_000 },
    "1440p": { width: 2560, height: 1440, videoBitsPerSecond: 10_000_000 },
    "4k": { width: 3840, height: 2160, videoBitsPerSecond: 20_000_000 },
  };

  // Capture the PROGRAM output canvas/video element
  const captureStream = useCallback((programElement) => {
    if (!programElement) return null;

    // If it's a video element, capture its stream
    if (programElement.tagName === "VIDEO" && programElement.captureStream) {
      return programElement.captureStream(30);
    }

    // If it's a canvas, capture
    if (programElement.tagName === "CANVAS" && programElement.captureStream) {
      return programElement.captureStream(30);
    }

    // Fallback: capture the entire element as a canvas via screen recording approach
    // Use the video element inside the program monitor
    const video = programElement.querySelector("video");
    if (video && video.captureStream) {
      return video.captureStream(30);
    }

    // Last resort: use getDisplayMedia (screen share) scoped to the element
    return null;
  }, []);

  // Create a new segment file (File System mode)
  const createSegmentWriter = useCallback(async () => {
    if (!dirHandleRef.current) return null;
    const idx = segmentIndexRef.current;
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `GLOTECH_REC_${timestamp}_seg${String(idx).padStart(3, "0")}.webm`;
    try {
      const fileHandle = await dirHandleRef.current.getFileHandle(filename, { create: true });
      const writable = await fileHandle.createWritable();
      segmentIndexRef.current = idx + 1;
      setSegmentCount(idx + 1);
      return writable;
    } catch (e) {
      setError(`Erreur ecriture disque: ${e.message}`);
      return null;
    }
  }, []);

  // Start recording
  const startRecording = useCallback(async (programElement, useFileSystem = false) => {
    setError(null);

    const detectedCodec = getCodec();
    if (!detectedCodec) {
      setError("Votre navigateur ne supporte pas l'enregistrement video");
      return false;
    }
    setCodec(detectedCodec);

    // Get the stream from the program element
    let stream = captureStream(programElement);

    // If no element stream, try screen capture as fallback
    if (!stream) {
      try {
        const q = qualityMap[quality];
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: { ideal: q.width }, height: { ideal: q.height }, frameRate: { ideal: 30 } },
          audio: true,
        });
      } catch (e) {
        setError("Impossible de capturer le flux video. Verifiez les permissions.");
        return false;
      }
    }

    streamRef.current = stream;

    // File System Access mode
    if (useFileSystem && fsSupported) {
      try {
        dirHandleRef.current = await window.showDirectoryPicker({ mode: "readwrite" });
        setMode("filesystem");
      } catch (e) {
        // User cancelled — fall back to memory mode
        dirHandleRef.current = null;
        setMode("memory");
      }
    } else {
      setMode("memory");
    }

    const q = qualityMap[quality];
    const recorder = new MediaRecorder(stream, {
      mimeType: detectedCodec,
      videoBitsPerSecond: q.videoBitsPerSecond,
    });
    recorderRef.current = recorder;
    chunksRef.current = [];
    fileSizeRef.current = 0;
    segmentIndexRef.current = 0;
    setSegmentCount(0);
    setFileSize(0);
    setDuration(0);

    // File System mode: create first segment writer
    if (dirHandleRef.current) {
      currentWriterRef.current = await createSegmentWriter();

      // Rotate segments every SEGMENT_DURATION_MS
      segmentTimerRef.current = setInterval(async () => {
        // Close current writer
        if (currentWriterRef.current) {
          try { await currentWriterRef.current.close(); } catch {}
        }
        // Open new segment
        currentWriterRef.current = await createSegmentWriter();
      }, SEGMENT_DURATION_MS);
    }

    recorder.ondataavailable = async (e) => {
      if (!e.data || e.data.size === 0) return;

      fileSizeRef.current += e.data.size;
      setFileSize(fileSizeRef.current);

      if (dirHandleRef.current && currentWriterRef.current) {
        // Write to disk continuously
        try {
          await currentWriterRef.current.write(e.data);
        } catch (err) {
          setError(`Erreur ecriture: ${err.message}`);
        }
      } else {
        // Memory mode: accumulate chunks
        chunksRef.current.push(e.data);
      }
    };

    recorder.onstop = async () => {
      // File System mode: close final writer
      if (currentWriterRef.current) {
        try { await currentWriterRef.current.close(); } catch {}
        currentWriterRef.current = null;
      }
      if (segmentTimerRef.current) {
        clearInterval(segmentTimerRef.current);
        segmentTimerRef.current = null;
      }

      // Memory mode: download the file
      if (!dirHandleRef.current && chunksRef.current.length > 0) {
        const blob = new Blob(chunksRef.current, { type: detectedCodec });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        a.href = url;
        a.download = `GLOTECH_REC_${timestamp}.webm`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }

      dirHandleRef.current = null;
      setRecording(false);
      setPaused(false);
    };

    recorder.start(TIMESLICE_MS);
    startTimeRef.current = Date.now();
    setRecording(true);
    setPaused(false);

    // Duration timer
    durationTimerRef.current = setInterval(() => {
      if (startTimeRef.current) {
        setDuration(Date.now() - startTimeRef.current);
      }
    }, 500);

    // If a display media stream track ends (user clicks "Stop sharing"), stop recording
    stream.getVideoTracks().forEach(track => {
      track.onended = () => stopRecording();
    });

    return true;
  }, [quality, fsSupported, captureStream, createSegmentWriter]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, []);

  // Pause / Resume
  const togglePause = useCallback(() => {
    if (!recorderRef.current) return;
    if (recorderRef.current.state === "recording") {
      recorderRef.current.pause();
      setPaused(true);
    } else if (recorderRef.current.state === "paused") {
      recorderRef.current.resume();
      setPaused(false);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      if (durationTimerRef.current) clearInterval(durationTimerRef.current);
      if (segmentTimerRef.current) clearInterval(segmentTimerRef.current);
    };
  }, []);

  return {
    recording,
    paused,
    duration,
    durationFormatted: formatDuration(duration),
    fileSize,
    fileSizeFormatted: formatSize(fileSize),
    segmentCount,
    mode,
    codec,
    quality,
    setQuality,
    error,
    fsSupported,
    startRecording,
    stopRecording,
    togglePause,
    formatDuration,
    formatSize,
  };
}
